<template>
    <div :class="classList">
        <display-content class="app-question__text" :value="question.text" />
        <div v-if="!question.is_type_section" class="app-question__answers">
            <slot v-if="findSlot('answer')" name="answer" v-bind="{ question, onAnswerSelected }" />

            <template v-else>
                <template v-if="question.is_type_multiple_choice">
                    <template v-for="(answer, index) in question.answers">
                        <app-question-answer
                            :key="`answer-${index}`"
                            :hide-checkbox="hideCheckbox"
                            :box-style="question.required ? 'radio' : 'checkbox'"
                            :answer="answer"
                            @answer:selected="onAnswerSelected"
                        />
                        <field-string
                            v-if="answer.value && answer.has_other_text"
                            :key="`answer-${index}-other`"
                            v-model="answer.other_text_value"
                            :label="answer.other_text_label"
                            :required="showError && (!question.is_valid || !question.is_complete)"
                            hide-details
                            class="mt-5"
                        />
                    </template>
                </template>

                <template v-else-if="question.is_type_string">
                    <field-string
                        :label="$t('answer')"
                        :value="_.get(question, 'answers[0].text')"
                        @input="onAnswerSelected"
                    />
                </template>

                <template v-else-if="question.is_type_textarea">
                    <field-description
                        :label="$t('answer')"
                        :value="_.get(question, 'answers[0].text')"
                        @input="onAnswerSelected"
                    />
                </template>

                <template v-else-if="question.is_type_wysiwyg">
                    <field-description
                        wysiwyg
                        hide-toolbar
                        inline
                        :value="_.get(question, 'answers[0].text')"
                        @input="onAnswerSelected"
                    />
                </template>
            </template>
        </div>
    </div>
</template>

<script>
import { AppQuestion, AppQuestionAnswer } from '@/models';
import uuid from '@/lib/helpers/uuid';
import diff from '@/lib/helpers/diff';
import { createEditor } from '@/lib/tiptap';

export default {
    name: 'AppQuestion',
    components: {
        AppQuestionAnswer: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppQuestionAnswer'
            )
    },
    props: {
        question: {
            type: AppQuestion,
            required: true
        },
        hideCheckbox: {
            type: Boolean,
            default: false
        },
        showError: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        classList() {
            let classList = ['app-question'];

            if (
                !this.question.is_type_section &&
                this.showError &&
                (!this.question.is_valid || !this.question.is_complete)
            ) {
                classList.push('app-question--error');
            }

            if (this.question.section) {
                classList.push('pl-4');
            }

            return classList;
        }
    },
    mounted() {
        this.question.setup();
    },
    methods: {
        onAnswerSelected(answer, value) {
            value = value === undefined ? true : value;

            if (this.question.is_type_wysiwyg && this.question.preset_answers.length) {
                const presetAnswer = createEditor({ content: this.question.preset_answers[0].text }).getJSON();
                const newAnswer = JSON.parse(JSON.stringify(answer));

                const differences = diff(presetAnswer, newAnswer, {
                    removeNull: true,
                    removeEmptyStrings: true,
                    removeEmptyObjects: true
                });

                value = Object.keys(differences).length > 0;
            }

            if (!this.question.is_type_multiple_choice) {
                answer = new AppQuestionAnswer({
                    id: uuid(36),
                    text: answer,
                    value: value,
                    correct: true
                });
            }

            this.question.set_answer(answer, value);
            this.$emit('answer:selected', answer);
        }
    }
};
</script>

<style lang="scss" scoped>
.app-question {
    display: block;
    margin-bottom: 2rem;
    color: rgba(0, 0, 0, 0.87);
    &__text {
        color: inherit;
        margin-bottom: 0.5rem;
    }
    // &--error {
    //     color: var(--v-error-darken1);
    // }
}
</style>
